import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { RequestStatus, REQUEST_STATUSES } from 'commons/types/common';
import { Meeting, MEETING_STATUSES } from 'commons/types/meetingsTypes';
import { ScheduleType } from 'components/schedule/ScheduleType';
import type { RootState } from 'stores/store';
import {
  createMeeting,
  updateMeeting,
  getMeetings,
  deleteMeeting,
} from './meetingsClient';

export interface MeetingsState {
  list?: Meeting[];
  next?: Meeting;
  createRequestStatus?: RequestStatus;
  updateRequestStatus?: RequestStatus;
  deleteRequestStatus?: RequestStatus;
  listRequestStatus?: RequestStatus;
}

const initialState: MeetingsState = {};

export const createMeetingAsync = createAsyncThunk(
  'meetings/createCurrent',
  (schedule: ScheduleType) => createMeeting(schedule),
);

export const updateMeetingAsync = createAsyncThunk(
  'meetings/updateCurrent',
  ({ id, meeting, serviceID }: ScheduleType) =>
    updateMeeting(id, meeting.date, serviceID),
);

export const deleteMeetingAsync = createAsyncThunk(
  'meetings/delete',
  ({ id, reason }: { id: string; reason: string }) => deleteMeeting(id, reason),
);

export const getMeetingsAsync = createAsyncThunk(
  'meetings/getList',
  (params: URLSearchParams = new URLSearchParams()) => getMeetings(params),
);

export const meetingsSlice = createSlice({
  name: 'meetings',
  initialState,
  reducers: {
    resetCurrentMeeting: (state) => {
      delete state.createRequestStatus;
      delete state.updateRequestStatus;
      delete state.deleteRequestStatus;
    },
    resetMeetingsList: (state) => {
      delete state.list;
      delete state.next;
      delete state.listRequestStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createMeetingAsync.pending, (state) => {
        state.createRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(createMeetingAsync.fulfilled, (state, { payload }) => {
        state.createRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.list?.unshift(payload);
        state.next = payload;
      })
      .addCase(createMeetingAsync.rejected, (state) => {
        state.createRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(updateMeetingAsync.pending, (state) => {
        state.updateRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(updateMeetingAsync.fulfilled, (state, { payload }) => {
        state.updateRequestStatus = REQUEST_STATUSES.SUCCESS;
        if (state.list && state.list.length > 0) {
          state.list[0] = payload;
        }
        state.next = payload;
      })
      .addCase(updateMeetingAsync.rejected, (state) => {
        state.updateRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(deleteMeetingAsync.pending, (state) => {
        state.deleteRequestStatus = REQUEST_STATUSES.PENDING;
        if (state.list) {
          state.list.shift();
        }
        delete state.next;
      })
      .addCase(deleteMeetingAsync.fulfilled, (state) => {
        state.deleteRequestStatus = REQUEST_STATUSES.SUCCESS;
      })
      .addCase(deleteMeetingAsync.rejected, (state) => {
        state.deleteRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getMeetingsAsync.pending, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getMeetingsAsync.fulfilled, (state, { payload }) => {
        state.listRequestStatus = REQUEST_STATUSES.SUCCESS;
        const sortedMeetings = payload.sort((a: Meeting, b: Meeting) =>
          dayjs(b.start_date).diff(a.start_date),
        );
        const activateMeeting = sortedMeetings.filter(
          (meeting: Meeting) => meeting.status === MEETING_STATUSES.ACCEPTED,
        );
        state.next =
          activateMeeting.length > 0 ? activateMeeting[0] : undefined;
        state.list = sortedMeetings;
      })
      .addCase(getMeetingsAsync.rejected, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.ERROR;
      });
  },
});

export const { resetCurrentMeeting, resetMeetingsList } = meetingsSlice.actions;

export const getMeetingsList = (state: RootState) => state.meetings.list;
export const getNextMeeting = (state: RootState) => state.meetings.next;
export const getCreateMeetingRequestStatus = (state: RootState) =>
  state.meetings.createRequestStatus;
export const getUpdateMeetingRequestStatus = (state: RootState) =>
  state.meetings.updateRequestStatus;
export const getDeleteMeetingRequestStatus = (state: RootState) =>
  state.meetings.deleteRequestStatus;
export const getMeetingsListRequestStatus = (state: RootState) =>
  state.meetings.listRequestStatus;

export default meetingsSlice.reducer;
